import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { Row } from 'antd';
import { MailOutlined, SettingOutlined } from '@ant-design/icons';
import BorderlessButton from '../../common/buttons/BorderlessButton';
import WorkflowHandle from './WorkflowHandle';
import WorkflowEmailConfigureDrawer from './WorkflowEmailConfigureDrawer';
import WorkflowActionNode from './WorkflowActionNode';
import FormWorkflowContext from './FormWorkflowContext';

const HOVER_TEXT = (
  <div>
    Emails added to this step will receive an email notification
    about the state of the workflow.
    <br />
    <br />
    We will send an invitation from <b>forms@ontraccr.com</b> to the
    email address.<br /><br /> The recipient must accept the invitation before Ontraccr
    will send emails to them.
  </div>
);

const isValidOffset = (offset) => (
  offset && offset.amount > 0 && offset.time
);

const offsetToDays = (offset) => (
  offset.time === 'weeks' ? offset.amount * 7 : offset.amount
);

const getErrorDetails = (nodeData) => {
  if (!nodeData?.users?.length) {
    return {
      content: (
        <div style={{ maxWidth: 300 }}>
          This step requires the <b>Select Recipient(s)</b> field to be configured.
        </div>
      ),
    };
  }
  if (nodeData?.scheduled) {
    const {
      scheduledAnchorOffset,
      scheduledAnchorFieldId,
      scheduledEndOffset,
      scheduledRepeat,
    } = nodeData;
    const hasDateField = !!scheduledAnchorFieldId;
    const hasValidOffset = isValidOffset(scheduledAnchorOffset);

    if (!hasDateField && !hasValidOffset) {
      return {
        content: (
          <div style={{ maxWidth: 300 }}>
            Scheduled email requires a <b>Start Sending</b> value
            or a linked date/time field.
          </div>
        ),
      };
    }
    if (scheduledRepeat && !isValidOffset(scheduledEndOffset)) {
      return {
        content: (
          <div style={{ maxWidth: 300 }}>
            Repeating email requires a <b>Stop Sending</b> value.
          </div>
        ),
      };
    }
    if (
      scheduledRepeat
      && !hasDateField
      && isValidOffset(scheduledEndOffset)
      && offsetToDays(scheduledEndOffset) <= offsetToDays(scheduledAnchorOffset)
    ) {
      return {
        content: (
          <div style={{ maxWidth: 300 }}>
            <b>Stop Sending</b> must be after <b>Start Sending</b>.
          </div>
        ),
      };
    }
  }
  return null;
};

export default ({
  setElements, setDataMap, isDisplay, divisionId, divisionIds = [], sections = [], isExternalForm, setDisableNext,
} = {}) => function WorkflowEmailNodeInner({
  id,
  draggable,
  data,
}) {
  const { dataMap = {} } = useContext(FormWorkflowContext) ?? {};
  const liveData = dataMap[id] || data;

  const [showDrawer, setShowDrawer] = useState(false);

  const [showErrorPopover, setShowErrorPopover] = useState(true);

  const openDrawer = useCallback(() => setShowDrawer(true), []);
  const closeDrawer = useCallback(() => setShowDrawer(false), []);

  const errorDetails = useMemo(() => getErrorDetails(liveData), [liveData]);

  useEffect(() => {
    if (!setDisableNext) return;
    const isInvalid = !!getErrorDetails(liveData);
    setDisableNext((prev) => ({ ...prev, [id]: isInvalid }));
    setShowErrorPopover(isInvalid);
  }, [id, setDisableNext, setShowErrorPopover, liveData]);

  return (
    <WorkflowActionNode
      title="Send an Email"
      Icon={MailOutlined}
      type="email"
      id={id}
      draggable={draggable}
      onNodeUpdate={(...args) => {
        setElements(...args);
        if (setDisableNext) {
          setDisableNext((prev) => ({ ...prev, [id]: false }));
        }
      }}
      isDisplay={isDisplay}
      hover={HOVER_TEXT}
      errorPopover={showErrorPopover ? errorDetails : undefined}
    >
      {!draggable && (
      <div>
        <Row style={{ margin: '20px 0px' }}>
          <BorderlessButton
            title="Configure"
            style={{ margin: 5 }}
            iconNode={<SettingOutlined />}
            onClick={openDrawer}
          />
        </Row>

        <WorkflowHandle type="target" position="top" disabled={isDisplay} />
        <WorkflowEmailConfigureDrawer
          id={id}
          visible={showDrawer}
          onClose={closeDrawer}
          onSubmit={closeDrawer}
          divisionId={divisionId}
          divisionIds={divisionIds}
          sections={sections}
          data={data}
          setDataMap={setDataMap}
          setElements={setElements}
          isExternalForm={isExternalForm}
          setDisableNext={setDisableNext}
          setShowErrorPopover={setShowErrorPopover}
        />
      </div>
      )}
    </WorkflowActionNode>
  );
};
